/* global __WEB_BUILD_ID__ */
import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { prepareMobileUpdate, UPDATE_ORIGIN } from '@/lib/mobileUpdatePolicy';

export default function LiveUpdates() {
  const [pending, setPending] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  async function apply() {
    if (!window.confirm('Update the app now? Finish and save any changes first.')) return;
    setApplying(true); setError('');
    try {
      const { LiveUpdate } = await import('@capawesome/capacitor-live-update');
      await LiveUpdate.reload();
    } catch { setError('Unable to apply the update. Please try again.'); }
    finally { setApplying(false); }
  }
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !Capacitor.isPluginAvailable('LiveUpdate')) return;
    let stopped = false;
    let running = false;
    let lastChecked = 0;
    let ready = false;
    let crashed = false;
    const onError = () => { crashed = true; };
    window.addEventListener('error', onError);
    const check = async () => {
      if (stopped || !ready || running || document.visibilityState === 'hidden' || Date.now() - lastChecked < 15 * 60_000) return;
      running = true;
      lastChecked = Date.now();
      try {
        const { LiveUpdate } = await import('@capawesome/capacitor-live-update');
        const result = await prepareMobileUpdate({ updater: LiveUpdate, builtInId: __WEB_BUILD_ID__,
          fetchManifest: async runtime => {
            const response = await fetch(`${UPDATE_ORIGIN}/mobile-updates/${runtime}/latest.json`,
              { cache: 'no-store', signal: AbortSignal.timeout(10_000) });
            return response.ok ? response.json() : null;
          },
        });
        if (!stopped) setPending(result === 'downloaded' || result === 'pending');
      } catch {
        // Offline, interrupted downloads and rejected signatures keep the installed bundle.
      } finally { running = false; }
    };
    // Only acknowledge a successfully rendered React tree, never the error-boundary fallback.
    const timer = setTimeout(async () => {
      if (stopped || crashed) return;
      try {
        const { LiveUpdate } = await import('@capawesome/capacitor-live-update');
        if (stopped || crashed) return;
        await LiveUpdate.ready();
        ready = true;
        void check();
      } catch { /* Older app installations continue using their bundled UI. */ }
    }, 2000);
    const interval = setInterval(() => { void check(); }, 60_000);
    const onOnline = () => { lastChecked = 0; void check(); };
    document.addEventListener('visibilitychange', check);
    window.addEventListener('online', onOnline);
    return () => {
      stopped = true;
      clearTimeout(timer);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('error', onError);
    };
  }, []);
  if (!pending) return null;
  return <aside role="status" className="fixed inset-x-3 z-[80] rounded-lg border bg-background p-3 shadow-lg" style={{ bottom: 'calc(env(safe-area-inset-bottom) + 5rem)' }}>
    <div className="flex items-center justify-between gap-3"><p className="text-sm">An app update is ready.</p><button type="button" className="shrink-0 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground" disabled={applying} onClick={apply}>{applying ? 'Updating...' : 'Update now'}</button></div>
    {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
  </aside>;
}
